import { ConfigApi } from '@d19n/youfibre-odin-sdk/dist/api-sdk-v2/ConfigApi';

// Polyfills for Buffer/process in browser builds
import { Buffer } from 'buffer';
import process from 'process';
import { unwrapData } from '../utils/helperFn';

if (typeof window !== 'undefined') {
  (window as any).Buffer = (window as any).Buffer || Buffer;
  (window as any).process = (window as any).process || process;
}

const stripProtocol = (host: string) => host.replace(/^https?:\/\//, '');

const formatPath = (urlOrPath: string) => {
  try {
    const u = new URL(urlOrPath);
    return `${u.pathname}${u.search}`.replace(/^\//, '');
  } catch {
    return urlOrPath.replace(/^\//, '');
  }
};

let customToken: string | undefined = undefined;

/**
 * Allow setting a custom token at runtime.
 */
export const setToken = (token?: string) => {
  customToken = token || undefined;
};

/**
 * Lazily create a ConfigApi instance.
 * Prevents Netlify build/SSR from crashing by avoiding init at module scope.
 */
const getOdinApi = () => {
  if (typeof window === 'undefined') {
    throw new Error('Odin API cannot be used during SSR/build.');
  }

  return new ConfigApi(
    {
      clientId: process.env.GATSBY_CLIENT_ID || '',
      clientSecret: process.env.GATSBY_CLIENT_SECRET || '',
      host: stripProtocol(process.env.GATSBY_API_URL || ''),
    },
    customToken
  );
};

let OdinAPI:any;

const isTokenExpired = (tokenExpiresAt, bufferMs = 30_000) => {
  return Date.now() >= tokenExpiresAt - bufferMs;
};

/**
 * Generic API caller
 */
const callApi = async (
  method: string,
  urlOrPath: string,
  body?: any,
  options: Record<string, any> = {}
) => {

    if(!OdinAPI || isTokenExpired(OdinAPI?.tokenExpiresAt)) OdinAPI = getOdinApi();

    const response = await OdinAPI.customApi({
        path: formatPath(urlOrPath),
        method: method.toUpperCase(),
        ...(body !== undefined && { body: (body instanceof FormData) ? body as unknown as BodyInit : body}),
        headers: body !== undefined ? { "Content-Type": (body instanceof FormData ? undefined : "application/json")}:{},
        ...options,
    }).catch((err: any) => {
        console.log('API error', err);    
        throw err;
    });
    
    localStorage.setItem('websocketUrl', process.env.GATSBY_TWILIO_WEBSOCKET || 'wss://ws.prod.youfibre.com');
    localStorage.setItem('apiUrl', process.env.GATSBY_API_URL || 'https://api.prod.youfibre.com');

    sessionStorage.setItem('authToken', OdinAPI.token || '');

    if(response){
        return  response.hasOwnProperty('data') ? 
            {...response, data: unwrapData(response)} : 
            { "statusCode": 201, "successful": true, "data": unwrapData(response)};
    } else {
        throw new Error('API response is undefined');
    }
};

/**
 * Exported API wrapper
 */
const API = {
  get: (url: string, options = {}) => 
    callApi('get', url, undefined, options),
  post: (url: string, body: any, options = {}) =>
    callApi('post', url, body, options),
  put: (url: string, body: any, options = {}) =>
    callApi('put', url, body, options),
  patch: (url: string, body: any, options = {}) =>
    callApi('patch', url, body, options),
  delete: (url: string, options = {}) =>
    callApi('delete', url, undefined, options),
};

export default API;